import React, { useState, useEffect } from 'react';
import QRGenerator from './QRGenerator';

interface BlogPost {
  title: string;
  slug: string;
  category: string[];
  finalUnsplashImageUrl: string;
  createdAt: string;
}

interface HomeProps {
  setPage: (page: string) => void;
  setSelectedBlogSlug?: (slug: string | null) => void;
}

export default function Home({ setPage, setSelectedBlogSlug }: HomeProps) {
  const [latestBlogs, setLatestBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchLatestBlogs() {
      try {
        const url = 'https://firestore.googleapis.com/v1/projects/getqrpost-7788e/databases/(default)/documents/blogs';
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load articles');
        const data = await res.json();
        
        if (data.documents && data.documents.length > 0) {
          const formatted: BlogPost[] = data.documents.map((doc: any) => {
            const fields = doc.fields;
            return {
              title: fields.title?.stringValue || '',
              slug: fields.slug?.stringValue || '',
              category: fields.category?.arrayValue?.values?.map((v: any) => v.stringValue) || ['Guides'],
              finalUnsplashImageUrl: fields.finalUnsplashImageUrl?.stringValue || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=600&auto=format&fit=crop',
              createdAt: fields.createdAt?.timestampValue || new Date().toISOString()
            };
          });
          
          formatted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          setLatestBlogs(formatted.slice(0, 3));
        }
      } catch (err) {
        console.error('Failed to fetch home articles:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchLatestBlogs();
  }, []);

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const faqData = [
      {
        question: "Do free QR codes expire?",
        answer: "No, the standard QR codes you create on our platform are permanent and will never expire or stop working."
      },
      {
        question: "Can I put my logo in the middle of the QR code?",
        answer: "Yes! You can easily upload your own business logo or image, and our tool will place it perfectly in the center without affecting the scan quality."
      },
      {
        question: "Do I need to pay to download a high-quality QR code?",
        answer: "No, you can download high-resolution PNG and print-ready SVG files completely for free, with no hidden fees."
      }
    ];

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map((item) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };

    const existingScript = document.getElementById('home-faq-jsonld-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'home-faq-jsonld-schema';
    script.innerHTML = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const tag = document.getElementById('home-faq-jsonld-schema');
      if (tag) {
        tag.remove();
      }
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleBlogClick = (slug: string) => {
    if (setSelectedBlogSlug) {
      setSelectedBlogSlug(slug);
    }
    setPage('blogs');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col gap-14 w-full">
      {/* Dynamic Hero Section */}
      <section className="text-center max-w-3xl mx-auto flex flex-col gap-5 py-4">
        <span className="self-center px-3 py-1 rounded-full bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 text-xs font-semibold uppercase tracking-wider animate-pulse">
          100% Free & Unlimited
        </span>
        <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
          <span className="bg-gradient-to-r from-gray-100 via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Privacy-First
          </span>{' '}
          <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            QR Generator Suite
          </span>
        </h1>
        <p className="text-base md:text-lg text-gray-400 leading-relaxed">
          Create premium, high-resolution custom QR codes for Wi-Fi access, WhatsApp chats, email templates, and websites. Secured client-side, zero subscriptions, and no registration required.
        </p>
      </section>

      {/* AI Bot Quick definition box (Generative Engine Optimization) */}
      <section className="max-w-3xl mx-auto w-full px-4 -mt-6 select-none">
        <div className="bg-gray-950 border border-gray-900 rounded-2xl p-5 flex flex-col gap-2 text-center md:text-left">
          <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Quick Definition</span>
          <p className="text-gray-300 text-sm leading-relaxed font-sans">
            <strong className="text-gray-100">What is a QR Code?</strong> A QR code is a scannable barcode that instantly connects smartphone users to websites, menus, or contact details without typing.
          </p>
        </div>
      </section>

      {/* Center QR Generation Suite */}
      <section className="w-full">
        <QRGenerator />
      </section>

      {/* Transparency Banner - No Hidden Catch */}
      <section className="w-full max-w-4xl mx-auto px-4 -mt-4 select-none">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border border-indigo-500/10 bg-indigo-950/5 rounded-2xl p-6 text-center font-sans">
          {[
            {
              title: "100% Free Forever",
              desc: "No trials, no paid tiers, and zero scan limit fees. All standard designs are completely public.",
              icon: (
                <svg className="h-5 w-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )
            },
            {
              title: "No Expiration Date",
              desc: "Static codes hold destination data directly and will remain functional permanently.",
              icon: (
                <svg className="h-5 w-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )
            },
            {
              title: "No Sign-Up Required",
              desc: "Generate and download print-ready codes instantly without email capture forms or account registration.",
              icon: (
                <svg className="h-5 w-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              )
            }
          ].map((item, idx) => (
            <div key={idx} className="flex flex-col items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-indigo-950/30 border border-indigo-500/10 flex items-center justify-center">
                {item.icon}
              </div>
              <h4 className="text-sm font-bold text-gray-200">{item.title}</h4>
              <p className="text-xs text-gray-400 max-w-[240px] leading-relaxed mx-auto">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Google AdSense Centered Placement Slot */}
      <div className="w-full max-w-4xl mx-auto p-4 bg-gray-900/10 border border-gray-900 border-dashed rounded-2xl text-center flex flex-col justify-center items-center h-28 select-none">
        <span className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Advertisement Space</span>
        <ins className="adsbygoogle"
             style={{ display: 'block' }}
             data-ad-client="ca-pub-XXXXXXXXXXXXXX"
             data-ad-slot="XXXXXXXXXXXXXX"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      </div>

      {/* Targeted Micro-Tool Solutions */}
      <section className="w-full max-w-4xl mx-auto flex flex-col gap-8 font-sans">
        <div className="flex flex-col gap-2 text-center md:text-left">
          <span className="px-3 py-1 rounded-full bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 text-xs font-semibold uppercase tracking-wider self-center md:self-start">
            Specialized Tools
          </span>
          <h2 className="text-3xl font-extrabold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-1">
            Targeted QR Micro-Tools
          </h2>
          <p className="text-gray-400 text-sm max-w-md">
            Optimize specific customer touchpoints with our free, standalone generation utilities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Free WiFi QR Code Generator",
              desc: "Create secure Wi-Fi access QR codes. Let guests scan to connect instantly without typing passwords.",
              page: "wifi-qr",
              icon: (
                <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071a10.5 10.5 0 0114.14 0M1.34 8.344a16.5 16.5 0 0122.628 0" />
                </svg>
              )
            },
            {
              title: "Free vCard QR Code Maker",
              desc: "Design premium digital business cards. Let prospects scan to save your contact info instantly.",
              page: "vcard-qr",
              icon: (
                <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.333 0 4 .8 4 2.4V18H5v-1.6c0-1.6 2.667-2.4 4-2.4z" />
                </svg>
              )
            },
            {
              title: "Free Restaurant Menu QR Creator",
              desc: "Generate custom QR codes for your PDF digital menu cards. Contactless, subscription-free dining.",
              page: "menu-qr",
              icon: (
                <svg className="h-6 w-6 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              )
            }
          ].map((item, idx) => (
            <div
              key={idx}
              onClick={() => {
                setPage(item.page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="group cursor-pointer bg-gray-900/30 border border-gray-900 hover:border-indigo-500/40 rounded-2xl p-6 flex flex-col gap-4 transition-all duration-300 backdrop-blur-sm shadow-md hover:shadow-xl hover:scale-[1.01]"
            >
              <div className="h-12 w-12 rounded-xl bg-gray-950 border border-gray-800 flex items-center justify-center group-hover:scale-105 transition-all duration-300 shrink-0">
                {item.icon}
              </div>
              <div className="flex flex-col gap-2 text-left">
                <h4 className="text-base font-bold text-gray-200 group-hover:text-indigo-400 transition-colors">
                  {item.title}
                </h4>
                <p className="text-gray-400 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
              <span className="text-[10px] uppercase font-bold text-indigo-400 group-hover:text-indigo-300 tracking-wider flex items-center gap-1 mt-auto select-none">
                Open Creator &rarr;
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Simple English Features and Guides Layout */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-gray-900 pt-12 max-w-4xl mx-auto w-full">
        {/* Step-by-Step Instructions */}
        <div className="bg-gray-900/20 border border-gray-900 rounded-2xl p-6 flex flex-col gap-4 backdrop-blur-sm">
          <h3 className="text-lg font-bold text-gray-100 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500"></span>
            How to Generate Your Free QR Code in 3 Steps
          </h3>
          <ol className="flex flex-col gap-3 text-sm text-gray-400 list-decimal list-inside">
            <li>
              <strong className="text-gray-300">Paste your Link or Text:</strong> Choose your type (Website, Wi-Fi, WhatsApp, or Email) and type in your parameters.
            </li>
            <li>
              <strong className="text-gray-300">Customize Style & Add Logo:</strong> Pick your custom colors, toggle sharp vs. smooth rounded corners, and upload your brand logo.
            </li>
            <li>
              <strong className="text-gray-300">Download PNG or Vector SVG:</strong> Instantly export standard PNG images or high-quality vector print SVG files.
            </li>
          </ol>
        </div>

        {/* Brand Value Propositions */}
        <div className="bg-gray-900/20 border border-gray-900 rounded-2xl p-6 flex flex-col gap-4 backdrop-blur-sm">
          <h3 className="text-lg font-bold text-gray-100 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500"></span>
            Why Choose GetQRPost?
          </h3>
          <ul className="flex flex-col gap-3 text-sm text-gray-400 list-disc list-inside">
            <li>
              <strong className="text-gray-300">100% Free & No Expiration:</strong> All generated QR codes are static and permanent. They will work forever with no scan limits.
            </li>
            <li>
              <strong className="text-gray-300">Privacy-First Engine:</strong> All compiles are generated inside your browser cache. We never track or store your input details.
            </li>
            <li>
              <strong className="text-gray-300">High-Resolution Formats:</strong> Export crisp, vector files ready for print media, restaurant menus, and large signs.
            </li>
          </ul>
        </div>
      </section>

      {/* Premium Interactive FAQ Section */}
      <section className="w-full border-t border-gray-900 pt-12 max-w-4xl mx-auto flex flex-col gap-8">
        <div className="text-center max-w-lg mx-auto">
          <h2 className="text-3xl font-extrabold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 text-sm">
            Everything you need to know about creating custom, high-resolution QR codes.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {[
            {
              question: "Do free QR codes expire?",
              answer: "No, the standard QR codes you create on our platform are permanent and will never expire or stop working."
            },
            {
              question: "Can I put my logo in the middle of the QR code?",
              answer: "Yes! You can easily upload your own business logo or image, and our tool will place it perfectly in the center without affecting the scan quality."
            },
            {
              question: "Do I need to pay to download a high-quality QR code?",
              answer: "No, you can download high-resolution PNG and print-ready SVG files completely for free, with no hidden fees."
            }
          ].map((item, idx) => {
            const isOpen = openFaqIndex === idx;
            return (
              <div
                key={idx}
                className="bg-gray-900/30 border border-gray-900 rounded-2xl overflow-hidden transition-all duration-300"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex items-center justify-between p-6 text-left text-gray-200 hover:text-gray-100 font-bold text-base md:text-lg focus:outline-none select-none transition-all"
                >
                  <span>{item.question}</span>
                  <span className={`h-7 w-7 rounded-xl bg-gray-900 border border-gray-800 flex items-center justify-center text-xs text-indigo-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    {isOpen ? '−' : '＋'}
                  </span>
                </button>

                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    isOpen ? 'max-h-40 border-t border-gray-900/60 bg-gray-900/10' : 'max-h-0'
                  }`}
                >
                  <p className="p-6 text-gray-400 text-sm md:text-base leading-relaxed text-justify">
                    {item.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SEO-Optimized Articles Grid Section */}
      <section className="w-full border-t border-gray-900 pt-12 flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-extrabold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-2">
              Latest Optimization Guides
            </h2>
            <p className="text-gray-400 text-sm">
              Discover how businesses leverage client-side QR codes for menus, hotspots, and contactless outreach.
            </p>
          </div>
          <button
            onClick={() => {
              if (setSelectedBlogSlug) setSelectedBlogSlug(null);
              setPage('blogs');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="self-start md:self-auto px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-gray-300 border border-gray-800 text-xs font-bold uppercase tracking-wider transition-all duration-200"
          >
            Explore All Guides &rarr;
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-72 w-full bg-gray-900/20 border border-gray-900 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        ) : latestBlogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">Latest guides are loading directly from our databases...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestBlogs.map((post) => (
              <div
                key={post.slug}
                onClick={() => handleBlogClick(post.slug)}
                className="group cursor-pointer bg-gray-900/40 border border-gray-900 hover:border-indigo-500/40 rounded-2xl overflow-hidden flex flex-col transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.01]"
              >
                <div className="h-44 w-full overflow-hidden relative">
                  <img
                    src={post.finalUnsplashImageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                  />
                  <span className="absolute top-4 left-4 bg-indigo-600/90 backdrop-blur-md px-2.5 py-1 rounded-md text-[10px] uppercase font-bold text-white tracking-wider">
                    {post.category[0]}
                  </span>
                </div>
                
                <div className="p-5 flex-1 flex flex-col justify-between gap-4">
                  <h3 className="text-base font-bold text-gray-100 group-hover:text-indigo-400 transition-colors duration-200 leading-snug">
                    {post.title}
                  </h3>
                  <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                    {new Date(post.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
